import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { IconArrowLeft, IconFileDescription } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { SegmentedControl } from "../../../../components/common/segmented-control";
import { SearchBar } from "../../../../components/common/input";
import { Button } from "../../../../components/common/button/button";
import { SimpleTable, type SimpleTableColumn } from "../../../../components/common/simple-table";
import { EmptyState } from "../../../../components/common/empty-state";
import { ShimmerLoader, Shimmer } from "../../../../components/common/shimmer";
import { Pagination } from "../../../../components/common/pagination";
import { PageContainer } from "../../../../components/common/page-container";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { useMePartner } from "../../../../swr";
import { useMyChangeRequests } from "../../hooks/useMyChangeRequests";
import { ChangeRequestStatusBadge } from "../components/change-request-status-badge";
import { humanizeTypeForOwner } from "../../data/change-request-type";
import { formatDate } from "../../../../utils/date";
import type { ChangeRequestDto, DocumentsQueryDto } from "../../../../api";
import { DocumentsQueryDtoStatusEnum } from "../../../../api";

type StatusTab = "all" | "PENDING" | "APPROVED" | "REJECTED";

const STATUS_TABS: { value: StatusTab; label: string }[] = [
  { value: "all", label: "All" },
  { value: "PENDING", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
];

const VALID_TABS = new Set<StatusTab>(STATUS_TABS.map((t) => t.value));
const PAGE_SIZE = 10;

function buildColumns(): SimpleTableColumn<ChangeRequestDto>[] {
  return [
    {
      key: "type",
      label: "Type",
      className: "w-[35%] min-w-0",
      render: (row) => (
        <Text variant="body" tone="dark">{humanizeTypeForOwner(row.type)}</Text>
      ),
    },
    {
      key: "status",
      label: "Status",
      className: "w-[20%]",
      render: (row) => <ChangeRequestStatusBadge status={row.status} />,
    },
    {
      key: "submittedAt",
      label: "Submitted",
      className: "w-[15%]",
      render: (row) => (
        <Text variant="body" tone="body">
          {row.submittedAt ? formatDate(row.submittedAt, true) : "—"}
        </Text>
      ),
    },
    {
      key: "updatedAt",
      label: "Last updated",
      className: "w-[15%]",
      render: (row) => (
        <Text variant="body" tone="body">{formatDate(row.updatedAt, true)}</Text>
      ),
    },
    {
      key: "action",
      label: "",
      className: "w-[15%] flex justify-end",
      render: () => (
        <Text variant="bodySmMedium" tone="accent" inline className="cursor-pointer">View</Text>
      ),
    },
  ];
}

export function MyChangeRequestsPage() {
  const navigate = useNavigate();
  const { isBuyer } = useMePartner();
  const [searchParams, setSearchParams] = useSearchParams();

  const statusParam = searchParams.get("status") as StatusTab | null;
  const statusTab: StatusTab = statusParam && VALID_TABS.has(statusParam) ? statusParam : "all";
  const search = searchParams.get("search") ?? "";
  const page = Number(searchParams.get("page") ?? "1") || 1;

  const { data, isLoading } = useMyChangeRequests({
    page,
    pageSize: PAGE_SIZE,
    search: search.trim() || undefined,
    status: statusTab === "all" ? undefined : (statusTab as DocumentsQueryDtoStatusEnum),
  } as DocumentsQueryDto);

  // Buyers see change requests scoped inside the account tab.
  if (isBuyer) {
    return <Navigate to={`${AppRoutes.ACCOUNT}?tab=change-requests`} replace />;
  }

  const requests = data?.data ?? [];
  const pageCount = data?.meta?.pageCount ?? 1;
  const columns = buildColumns();

  const setStatusTab = (next: StatusTab) => {
    const p = new URLSearchParams(searchParams);
    if (next === "all") p.delete("status");
    else p.set("status", next);
    p.delete("page");
    setSearchParams(p);
  };

  const isFiltered = statusTab !== "all" || search.trim() !== "";

  return (
    <PageContainer>
      <div className="flex w-full flex-col gap-6">
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<IconArrowLeft size={16} />}
          onClick={() => navigate(AppRoutes.SETTINGS)}
          className="self-start !px-0"
        >
          Back to settings
        </Button>
        <div className="flex flex-col gap-1">
          <Heading variant="h2" tone="dark">My Change Requests</Heading>
          <Text variant="bodySm" tone="muted">
            Track every profile, product and account edit you've submitted for review.
          </Text>
        </div>

        <div className="flex items-center gap-4 max-md:flex-col max-md:items-stretch max-md:gap-3">
          <SegmentedControl<StatusTab>
            options={STATUS_TABS}
            value={statusTab}
            onChange={setStatusTab}
            aria-label="Filter change requests by status"
            className="max-md:self-start"
          />
          <div className="flex-1 max-md:flex-none">
            <SearchBar
              value={search}
              onChange={(e) => {
                const p = new URLSearchParams(searchParams);
                if (e.target.value.trim()) p.set("search", e.target.value);
                else p.delete("search");
                p.delete("page");
                setSearchParams(p);
              }}
              placeholder="Search by request ID or note"
            />
          </div>
        </div>

        {isLoading ? (
          <ShimmerLoader message="Fetching your change requests…">
            <div className="flex flex-col gap-2">
              {Array.from({ length: 7 }).map((_, i) => (
                <Shimmer key={i} className="h-14 w-full" />
              ))}
            </div>
          </ShimmerLoader>
        ) : requests.length === 0 ? (
          <div className="rounded-2xl border border-borderDefault bg-bgWhite">
            <EmptyState
              icon={IconFileDescription}
              title={
                isFiltered
                  ? "No change requests match your filters"
                  : "No change requests yet"
              }
              description={
                isFiltered
                  ? "Try clearing the search or switching to the All tab."
                  : "Submit a profile or product edit to see it here once it goes into review."
              }
            />
          </div>
        ) : (
          <SimpleTable
            columns={columns}
            data={requests}
            keyExtractor={(r) => r.id}
            onRowClick={(r) => navigate(`${AppRoutes.CHANGE_REQUESTS}/${r.id}`)}
            footer={
              pageCount > 1 ? (
                <div className="flex justify-center">
                  <Pagination
                    page={page}
                    pageCount={pageCount}
                    onPageChange={(next) => {
                      const p = new URLSearchParams(searchParams);
                      p.set("page", String(next));
                      setSearchParams(p, { replace: true });
                    }}
                  />
                </div>
              ) : undefined
            }
          />
        )}
      </div>
    </PageContainer>
  );
}
